<template>
  <div style="width: 100vw; height: 100vh; overflow: hidden">
    <VSnackbar
      v-model="showErrorMessage"
      :timeout="timeout"
      :top="true"
      color="error"
      location="top"
    >
      <i right class="fa-regular fa-exclamation-triangle text-white" />
      <span class="ml-4 text-white">
        {{ errorMessage }}
      </span>
    </VSnackbar>

    <div v-if="!showForm">
      <VRow class="pt-4" justify="space-between" align="center">
        <VCol cols="2">
          <VBtn icon="$close" class="mx-3" variant="text" @click="returnPage" />
        </VCol>
        <VCol cols="6" align="center">
          <VRow justify="center">
            <VCol>
              {{ $t("bilan.bilanInaccessible") }}
            </VCol>
          </VRow>
        </VCol>
        <VCol cols="2" />
      </VRow>

      <VRow justify="center">
        <VBtn color="success" small @click="downloadPdf()">{{ $t("labelText.download") }} </VBtn>
      </VRow>

      <VRow justify="center">
        <VCol cols="10">
          <VCard>
            <iframe :src="bilanResult" title="PDF" width="100%" height="580" />
          </VCard>
        </VCol>
      </VRow>
    </div>

    <div v-show="showForm" class="pt-4 fill-height align-content-center">
      <VRow align="center" justify="center">
        <VCol cols="8" lg="5">
          <VCard class="py-10" @keydown.enter="verifyForm">
            <VCardTitle class="text-center">
              {{ $t("bilan.bilanTitle") }}
            </VCardTitle>

            <VCardText class="text-center mt-12">
              <p>{{ $t("bilan.bilanText") }}</p>
              <p>{{ $t("bilan.bilanText2") }}</p>
            </VCardText>

            <VCardText>
              <VForm ref="form">
                <VRow justify="center">
                  <VCol cols="10">
                    <VTextField
                      v-model="code"
                      :label="$t('bilan.bilanCode')"
                      maxlength="5"
                      autofocus
                      :rules="rules"
                      required
                    />
                  </VCol>
                  <VCol cols="10">
                    <VTextField
                      v-model="name"
                      :label="$t('bilan.bilanName')"
                      :rules="rules"
                      required
                    />
                  </VCol>
                  <VCol cols="10">
                    <VSwitch v-model="hideIdentity" :label="$t('bilan.bilanNoId')" />
                  </VCol>
                </VRow>

                <VRow justify="center" v-if="!complementForm">
                  <VCol cols="10" v-if="complementFormElements.includes('patient_last_name')">
                    <VTextField
                      v-model="patientName"
                      :rules="[value => !!value || $t('fieldErrors.fieldRequired')]"
                      :label="$t('bilan.bilanPatientName')"
                      required
                    />
                  </VCol>
                  <VCol cols="10" v-if="complementFormElements.includes('patient_zip_code')">
                    <VTextField v-model="zipcode" :label="$t('bilan.zipcode')" />
                  </VCol>
                  <VCol cols="10" v-if="complementFormElements.includes('patient_birth_date')">
                    <BDateInput
                      v-model="birthDate"
                      :label="$t('bilan.birthDate')"
                      hideDetails="auto"
                    />
                  </VCol>
                  <VCol
                    cols="10"
                    v-if="complementFormElements.includes('patient_birthplace_zipcode')"
                  >
                    <VTextField v-model="birthZipcode" :label="$t('bilan.birthZipcode')" />
                  </VCol>
                </VRow>
              </VForm>
            </VCardText>
            <VCardActions>
              <VRow justify="center">
                <VCol cols="6">
                  <VBtn
                    :loading="loading"
                    :disabled="loading"
                    color="success"
                    variant="elevated"
                    block
                    @click="verifyForm"
                  >
                    {{ $t("bilan.bilanBtn") }}
                  </VBtn>
                </VCol>
              </VRow>
            </VCardActions>
          </VCard>
        </VCol>
      </VRow>
      <VRow>
        <VCol class="text-center">
          <a :href="needHelpUrl" target="_blank">{{ $t("labelText.needHelp") }}</a>
        </VCol>
      </VRow>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axios from "axios";

import bilanApiService from "@/services/bilan";
import config from "@/config/env";
import BDateInput from "@/components/bisom/input/BDateInput.vue";

/** IE / legacy Edge — not in standard `Navigator` typings */
type NavigatorWithMsBlob = Navigator & {
  msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean;
};

export default defineComponent({
  name: "BilanPage",

  components: { BDateInput },

  data() {
    return {
      rules: [
        (value: string | null) => !!value || this.$t("fieldErrors.fieldRequired"),
        (value: string | null) =>
          (value && value.length >= config.validations.bilanNameMinSize) ||
          this.$t("fieldErrors.minSize", { minSize: config.validations.bilanNameMinSize }),
      ],
      loading: false,
      bilanResult: "",
      name: "",
      code: "",
      hideIdentity: false,
      showForm: true,
      btnReturn: false,
      showErrorMessage: false,
      errorMessage: "" as string | unknown,
      timeout: config.snackbarTimeout,
      complementFormElements: [] as string[],
      complementForm: false,
      zipcode: "",
      patientName: "",
      birthDate: "",
      birthZipcode: "",
      menu: false,
      today: new Date().toISOString().substr(0, 10),
      needHelpUrl: config.need_help_url,
      resultPdf: undefined as undefined | { data: Blob; name: string },
    };
  },

  methods: {
    async verifyForm() {
      const { valid } = await Object(this.$refs.form).validate();
      if (valid) {
        if (this.complementForm) {
          this.getExpiredBilan();
        } else {
          this.getBilan();
        }
      }
    },

    returnPage() {
      this.complementForm = false;
      this.showForm = true;
      this.code = "";
      this.name = "";
      this.hideIdentity = false;
      this.patientName = "";
      this.zipcode = "";
      this.birthZipcode = "";
      this.birthDate = "";
      this.bilanResult = "";
      Object(this.$refs.form).resetValidation();
    },

    async getBilan() {
      try {
        this.loading = true;

        const response = await bilanApiService.getBilan(
          this.code.toUpperCase(),
          this.name,
          this.hideIdentity
        );

        if (response.json) {
          // if pdf expired, need more info -> update form
          this.complementFormElements = response.data;
          this.complementForm = true;
        } else {
          //if pdf
          this.resultPdf = response;
          this.showPdf(response);
        }
      } catch (error: unknown) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        this.notifyError(status ? this.$t("serverErrors." + status) : this.$t("labelText.error"));
      } finally {
        this.loading = false;
      }
    },

    async downloadPdf() {
      try {
        this.loading = true;

        const response = this.resultPdf
          ? this.resultPdf
          : await bilanApiService.getBilan(this.code.toUpperCase(), this.name, this.hideIdentity);

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(response.data);
        link.download = response.name;
        link.click();
      } catch (error: unknown) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        this.notifyError(status ? this.$t("serverErrors." + status) : this.$t("labelText.error"));
      } finally {
        this.loading = false;
      }
    },

    async getExpiredBilan() {
      try {
        this.loading = true;
        const bodyRequest = {
          recipient_name: this.name,
          patient_last_name: this.patientName,
          patient_birth_date: this.birthDate,
          patient_birthplace_zipcode: this.birthZipcode,
          patient_zip_code: this.zipcode,
          without_identity: this.hideIdentity,
        };

        const response = await bilanApiService.getExpiredBilan(
          this.code.toUpperCase(),
          bodyRequest
        );
        this.showPdf(response);
      } catch (error: unknown) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        this.notifyError(status ? this.$t("serverErrors." + status) : this.$t("labelText.error"));
      } finally {
        this.loading = false;
      }
    },

    showPdf(response: { data: Blob; name: string }) {
      try {
        const nav = window.navigator as NavigatorWithMsBlob;
        if (nav.msSaveOrOpenBlob) {
          nav.msSaveOrOpenBlob(response.data, response.name + ".pdf");
        } else {
          const url = window.URL.createObjectURL(response.data);
          this.bilanResult = url;
          this.showForm = false;
        }
      } catch (error) {
        this.notifyError(error);
      }
    },

    save(day: string) {
      Object(this.$refs.menu).save(day);
    },

    notifyError(message: string | unknown) {
      this.showErrorMessage = true;
      this.errorMessage = message;
    },
  },
});
</script>

<style scoped>
a {
  text-decoration: none;
}
</style>
